import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDowngradeWizard } from '../../hooks/useDowngradeWizard';
import { Modal } from '../ui/modal';
import { Button } from '../ui/Button';
import WelcomeStep from './steps/WelcomeStep';
import FamilySelectionStep from './steps/FamilySelectionStep';
import MemberSelectionStep from './steps/MemberSelectionStep';
import ActivitySelectionStep from './steps/ActivitySelectionStep';
import ChecklistSelectionStep from './steps/ChecklistSelectionStep';
import RecipeSelectionStep from './steps/RecipeSelectionStep';
import ShoppingListSelectionStep from './steps/ShoppingListSelectionStep';
import BlockedFeaturesStep from './steps/BlockedFeaturesStep';
import SummaryStep from './steps/SummaryStep';

/**
 * @fileoverview Wizard principal para downgrade de plan
 * Guía al usuario a través del proceso de selección de contenido a mantener
 */

interface DowngradeWizardProps {
  fromPlanId: string;
  toPlanId?: string;
  isOpen: boolean;
  onComplete: () => void;
  onCancel: () => void;
}

export default function DowngradeWizard({
  fromPlanId,
  toPlanId = 'free',
  isOpen,
  onComplete,
  onCancel,
}: DowngradeWizardProps) {
  const { t } = useTranslation();
  const {
    loading,
    error,
    comparison,
    currentCounts,
    recommendations,
    selections,
    steps,
    currentStep,
    currentStepIndex,
    canGoBack,
    canGoNext,
    isLastStep,
    initialize,
    nextStep,
    prevStep,
    updateSelection,
    validateSelections,
    applyDowngrade,
  } = useDowngradeWizard(fromPlanId, toPlanId);
  
  // Inicializar al abrir
  React.useEffect(() => {
    if (isOpen && !comparison) {
      initialize();
    }
  }, [isOpen, comparison, initialize]);
  
  // Manejar completado
  const handleComplete = async () => {
    const success = await applyDowngrade();
    if (success) {
      onComplete();
    }
  };
  
  // Debug: ver pasos generados
  useEffect(() => {
    if (steps.length > 0) {
      console.log('🔍 Pasos del wizard generados:', steps);
      console.log('🔍 Paso actual:', currentStep);
      console.log('🔍 Comparison:', comparison);
    }
  }, [steps, currentStep, comparison]);
  
  // Renderizar paso actual
  const renderStep = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('downgradeWizard.loadingInfo')}</p>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="py-12 text-center">
          <div className="text-red-600 dark:text-red-400 mb-4">
            <span className="text-4xl">⚠️</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('downgradeWizard.errorTitle')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <Button onClick={() => initialize()}>{t('downgradeWizard.retry')}</Button>
        </div>
      );
    }
    
    if (!comparison || !currentStep) {
      return (
        <div className="py-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">{t('downgradeWizard.preparing')}</p>
        </div>
      );
    }
    
    const stepProps = {
      comparison,
      currentCounts,
      recommendations,
      selections,
      onUpdateSelection: updateSelection,
      onNext: nextStep,
      onBack: prevStep,
    };
    
    // Helper para obtener el límite de un feature
    const getFeatureLimit = (feature: string): number => {
      const change = comparison?.changes.find(c => c.feature === feature);
      return change?.selectionLimit || 0;
    };
    
    switch (currentStep.component) {
      case 'welcome':
        return <WelcomeStep {...stepProps} />;
      
      case 'groups':
        return (
          <FamilySelectionStep
            selections={selections}
            onUpdateSelections={(update) => {
              Object.entries(update).forEach(([key, value]) => {
                updateSelection(key as keyof typeof selections, value);
              });
            }}
            limit={getFeatureLimit('families')}
            recommendations={recommendations?.families}
          />
        );
      
      case 'members':
        return (
          <MemberSelectionStep
            selections={selections}
            onUpdateSelections={(update) => {
              Object.entries(update).forEach(([key, value]) => {
                updateSelection(key as keyof typeof selections, value);
              });
            }}
            limit={getFeatureLimit('members')}
            recommendations={recommendations?.members}
          />
        );
      
      case 'activities':
        return <ActivitySelectionStep {...stepProps} />;
      
      case 'checklists':
        return <ChecklistSelectionStep {...stepProps} />;
      
      case 'recipes':
        return <RecipeSelectionStep {...stepProps} />;
      
      case 'shopping':
        return <ShoppingListSelectionStep {...stepProps} />;
      
      case 'blocked':
        return <BlockedFeaturesStep {...stepProps} />;
      
      case 'summary':
        return <SummaryStep {...stepProps} />;
      
      default:
        return (
          <div className="py-12 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              Paso no reconocido: {currentStep.component}
            </p>
          </div>
        );
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      showCloseButton={false}
      className="w-[95vw] max-w-6xl h-[95vh] overflow-hidden"
    >
      <div className="flex flex-col h-full p-6">
        {/* Progress bar */}
        {steps.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('downgradeWizard.step', { current: currentStepIndex + 1, total: steps.length })}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {currentStep?.title}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-300"
                style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Step content */}
        <div className="flex-1 overflow-y-auto mb-6 pr-2">
          <div className="max-w-4xl mx-auto">
            {renderStep()}
          </div>
        </div>
        
        {/* Navigation buttons */}
        {!loading && !error && comparison && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div>
              {canGoBack && (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={loading}
                >
                  ← {t('downgradeWizard.back')}
                </Button>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={loading}
              >
                {t('common.cancelar')}
              </Button>
              
              {isLastStep ? (
                <Button
                  onClick={handleComplete}
                  disabled={loading || !validateSelections().valid}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  {loading ? t('downgradeWizard.applying') : `✅ ${t('downgradeWizard.confirmChanges')}`}
                </Button>
              ) : (
                <Button
                  onClick={nextStep}
                  disabled={loading || !canGoNext}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  {t('downgradeWizard.continue')} →
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
